import { useState } from "react";
import { Trash2, ChevronLeft, ChevronRight, Linkedin } from "lucide-react";
import { WatchlistProvider, useWatchlist } from "./modal/WatchlistContext";
import ConfirmModal from "./ui/ConfirmModal";
import { bulkDeletePosts } from "./api/WatchlistApi";
import type { WatchlistPost } from "./api/WatchlistApi";

// ── Raw Posts ─────────────────────────────────────────────────────────────────

function RawPostRow({
  post,
  onDelete,
}: {
  post: WatchlistPost;
  onDelete: (id: number) => Promise<string | null>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleDelete() {
    setBusy(true);
    setError("");
    const err = await onDelete(post.id);
    if (err) setError(err);
    setBusy(false);
  }

  const STATUS_STYLES: Record<string, string> = {
    new: "bg-blue-500 text-white border-blue-400",
    relevant: "bg-emerald-500 text-emerald-950 border-emerald-400",
    archived: "bg-muted text-muted-foreground border-border",
  };

  const displayDate = post.post_date
    ? new Date(post.post_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-surface-muted/30 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={post.post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:text-primary/80 font-inter truncate max-w-xs transition-colors"
          >
            {post.post_url}
          </a>
          <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${STATUS_STYLES[post.status] ?? STATUS_STYLES.new}`}>
            {post.status}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground font-inter">
          <span className="inline-flex items-center gap-1">
            {post.target_display_name || "Unknown"}
            {post.target_linkedin_url && (
              <a href={post.target_linkedin_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-sky-400">
                <Linkedin className="w-3 h-3" />
              </a>
            )}
          </span>
          {displayDate && <span>{displayDate}</span>}
        </div>
        {post.post_text && (
          <p className="text-[11px] text-muted-foreground font-inter mt-1 line-clamp-2 leading-relaxed">
            {post.post_text}
          </p>
        )}
        {error && <p className="text-[11px] text-amber-400 font-inter mt-0.5">{error}</p>}
      </div>
      <button
        onClick={handleDelete}
        disabled={busy}
        title="Delete"
        className="p-1.5 rounded hover:bg-surface-muted transition-colors disabled:opacity-50 text-muted-foreground hover:text-destructive shrink-0"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function RawPostsTab() {
  const {
    posts, postsLoading,
    postFilters, totalPosts, totalPostPages,
    setPostFilters, deletePost, loadPosts,
  } = useWatchlist();

  const [cleanupDays, setCleanupDays] = useState<number>(30);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<string>("");
  const [confirmCleanup, setConfirmCleanup] = useState(false);

  const PERIOD_LABELS: Record<number, string> = { 7: "1 week", 30: "1 month", 90: "3 months", 180: "6 months", 365: "1 year" };

  async function handleBulkDelete() {
    setConfirmCleanup(false);
    setCleaning(true);
    setCleanupResult("");
    try {
      const { deleted } = await bulkDeletePosts(cleanupDays);
      setCleanupResult(`Deleted ${deleted} post${deleted !== 1 ? "s" : ""}.`);
      await loadPosts();
    } catch {
      setCleanupResult("Failed to delete posts.");
    } finally {
      setCleaning(false);
    }
  }

  function goToPage(page: number) {
    setPostFilters({ page });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground font-inter">
          Raw scraped posts used for deduplication. Safe to delete periodically.
        </p>
        <div className="flex items-center gap-2">
          <select
            value={cleanupDays}
            onChange={(e) => { setCleanupDays(Number(e.target.value)); setCleanupResult(""); }}
            className="bg-surface border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary font-inter appearance-none"
          >
            <option value={7}>Older than 1 week</option>
            <option value={30}>Older than 1 month</option>
            <option value={90}>Older than 3 months</option>
            <option value={180}>Older than 6 months</option>
            <option value={365}>Older than 1 year</option>
          </select>
          <button
            onClick={() => setConfirmCleanup(true)}
            disabled={cleaning}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 text-sm font-inter rounded-xl transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {cleaning ? "Deleting…" : "Delete"}
          </button>
          {cleanupResult && (
            <span className="text-xs text-muted-foreground font-inter">{cleanupResult}</span>
          )}
        </div>
      </div>
      <div className="flex justify-end">
        <span className="text-xs text-muted-foreground font-inter">{totalPosts} post{totalPosts !== 1 ? "s" : ""}</span>
      </div>

      <div className="bg-surface/60 border border-border/60 rounded-2xl overflow-hidden">
        {postsLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!postsLoading && posts.length === 0 && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <p className="font-inter text-sm">No raw posts.</p>
          </div>
        )}

        {!postsLoading && posts.map((post) => (
          <RawPostRow key={post.id} post={post} onDelete={deletePost} />
        ))}
      </div>

      {totalPostPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => goToPage(postFilters.page - 1)}
            disabled={postFilters.page <= 1}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-muted-foreground font-inter">
            Page {postFilters.page} of {totalPostPages}
          </span>
          <button
            onClick={() => goToPage(postFilters.page + 1)}
            disabled={postFilters.page >= totalPostPages}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {confirmCleanup && (
        <ConfirmModal
          title="Delete Old Posts"
          message={`Delete all raw posts older than ${PERIOD_LABELS[cleanupDays]}?`}
          onConfirm={handleBulkDelete}
          onClose={() => setConfirmCleanup(false)}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WatchlistPage() {
  return (
    <WatchlistProvider>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">LinkedIn Watchlist Posts</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            Raw posts scraped by n8n from companies/contacts marked "Watching" in{" "}
            <a href="/admin/hiring-contacts" className="text-primary hover:text-primary/80 underline">
              LinkedIn Hiring Contacts
            </a>.
          </p>
        </div>
        <RawPostsTab />
      </div>
    </WatchlistProvider>
  );
}
